import React, { useId } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Typography,
} from '@mui/material'
import type {
	ButtonProps,
	DialogActionsProps,
	DialogContentProps,
	DialogContentTextProps,
	DialogProps,
	DialogTitleProps,
	IconButtonProps,
	SxProps,
	Theme,
} from '@mui/material'
import CustomButton from '../../v2/general/CustomButton/CustomButton'

export type CustomAlertDialogSeverity = 'error' | 'warning' | 'info' | 'success' | 'question'

export type CustomAlertDialogCloseReason =
	| 'backdropClick'
	| 'escapeKeyDown'
	| 'closeButtonClick'
	| 'actionClick'
	| string

export interface CustomAlertDialogAction {
	id?: string
	label: React.ReactNode
	onClick?: React.MouseEventHandler<HTMLButtonElement>
	closeOnClick?: boolean
	closeReason?: CustomAlertDialogCloseReason
	variant?: ButtonProps['variant']
	color?: ButtonProps['color']
	size?: ButtonProps['size']
	disabled?: boolean
	autoFocus?: boolean
	isLoading?: boolean
	startIcon?: React.ReactNode
	endIcon?: React.ReactNode
	sx?: SxProps<Theme>
	buttonProps?: Omit<
		ButtonProps,
		'children' | 'onClick' | 'variant' | 'color' | 'size' | 'disabled' | 'autoFocus' | 'startIcon' | 'endIcon' | 'sx'
	>
}

export interface CustomAlertDialogProps extends Omit<DialogProps, 'title'> {
	title?: React.ReactNode
	description?: React.ReactNode
	severity?: CustomAlertDialogSeverity
	icon?: React.ReactNode
	hideIcon?: boolean
	showCloseButton?: boolean
	closeButtonAriaLabel?: string
	closeOnBackdropClick?: boolean
	closeOnEscapeKeyDown?: boolean
	onRequestClose?: (reason: CustomAlertDialogCloseReason) => void
	primaryAction?: CustomAlertDialogAction
	secondaryAction?: CustomAlertDialogAction
	actions?: CustomAlertDialogAction[]
	actionsContent?: React.ReactNode
	dialogTitleProps?: DialogTitleProps
	dialogContentProps?: DialogContentProps
	dialogContentTextProps?: DialogContentTextProps
	dialogActionsProps?: DialogActionsProps
	closeButtonProps?: Omit<IconButtonProps, 'onClick' | 'aria-label'>
	iconContainerSx?: SxProps<Theme>
	headerSx?: SxProps<Theme>
}

type CustomAlertDialogLayout = 'inline' | 'stacked'

const severityConfig: Record<
	CustomAlertDialogSeverity,
	{
		icon: React.ReactNode
		color: string
		background: string
		layout: CustomAlertDialogLayout
		iconSize: string
		iconBoxShadow?: string
		paperBackground?: string
		paperBoxShadow?: string
		paperBorderRadius?: string
		stackedGap?: string
		titleSx?: SxProps<Theme>
		stackedActionsPadding?: string
		stackedActionsGap?: string
	}
> = {
	error: {
		icon: <ErrorOutlineRoundedIcon sx={{ fontSize: 35 }} />,
		color: '#FFFFFF',
		background: 'linear-gradient(90deg, #D32F2F 0%, #FB736D 100%)',
		layout: 'stacked',
		iconSize: '67px',
		paperBackground: 'linear-gradient(180deg, rgba(211, 47, 47, 0.10) 0%, rgba(211, 47, 47, 0) 100%)',
		paperBoxShadow:
			'0 6px 30px 5px rgba(9, 32, 86, 0.12), 0 16px 24px 2px rgba(9, 32, 86, 0.14), 0 8px 10px -5px rgba(9, 32, 86, 0.20)',
		paperBorderRadius: '4px',
		stackedGap: '0px',
		titleSx: {
			display: 'block',
			fontFamily: 'Poppins, sans-serif',
			fontSize: '1rem',
			fontWeight: 600,
			lineHeight: 1.75,
			letterSpacing: '0.15px',
			color: 'var(--primary-dark)',
			textAlign: 'left',
		},
		stackedActionsPadding: '8px 24px',
		stackedActionsGap: '8px',
	},
	warning: {
		icon: <WarningAmberRoundedIcon sx={{ fontSize: 34 }} />,
		color: '#FFFFFF',
		background: 'linear-gradient(135deg, #FF6B00 0%, #F6BE4C 100%)',
		layout: 'stacked',
		iconSize: '67px',
		iconBoxShadow: '0 16px 30px rgba(255, 144, 0, 0.28)',
		paperBackground: 'linear-gradient(180deg, #FFF7F0 0%, #FFFFFF 58%)',
		paperBoxShadow: '0 18px 40px rgba(9, 32, 86, 0.18), 0 0 32px rgba(0, 110, 255, 0.18)',
		paperBorderRadius: '4px',
		stackedGap: '0px',
		titleSx: {
			display: 'block',
			fontFamily: 'Poppins, sans-serif',
			fontSize: '1.125rem',
			fontWeight: 600,
			lineHeight: '1.55',
			color: 'var(--primary-dark)',
			textAlign: 'left',
		},
		stackedActionsPadding: '8px 24px',
		stackedActionsGap: '8px',
	},
	info: {
		icon: <InfoOutlinedIcon sx={{ fontSize: 35 }} />,
		color: '#FFFFFF',
		background: 'linear-gradient(90deg, #00A5FF 0%, #37CDFF 100%)',
		layout: 'stacked',
		iconSize: '67px',
		paperBackground: 'linear-gradient(180deg, rgba(0, 165, 255, 0.10) 0%, rgba(0, 165, 255, 0) 100%)',
		paperBoxShadow:
			'0 6px 30px 5px rgba(9, 32, 86, 0.12), 0 16px 24px 2px rgba(9, 32, 86, 0.14), 0 8px 10px -5px rgba(9, 32, 86, 0.20)',
		paperBorderRadius: '4px',
		stackedGap: '0px',
		titleSx: {
			display: 'block',
			fontFamily: 'Poppins, sans-serif',
			fontSize: '1rem',
			fontWeight: 600,
			lineHeight: 1.75,
			letterSpacing: '0.15px',
			color: 'var(--primary-dark)',
			textAlign: 'left',
		},
		stackedActionsPadding: '8px 24px',
		stackedActionsGap: '8px',
	},
	success: {
		icon: <CheckCircleOutlineRoundedIcon sx={{ fontSize: 35 }} />,
		color: '#FFFFFF',
		background: 'linear-gradient(65deg, #16C53D 57.784%, #00A5FF 115.35%)',
		layout: 'stacked',
		iconSize: '67px',
		paperBackground: 'linear-gradient(180deg, rgba(22, 197, 61, 0.10) 0%, rgba(22, 197, 61, 0) 100%)',
		paperBoxShadow:
			'0 6px 30px 5px rgba(9, 32, 86, 0.12), 0 16px 24px 2px rgba(9, 32, 86, 0.14), 0 8px 10px -5px rgba(9, 32, 86, 0.20)',
		paperBorderRadius: '4px',
		stackedGap: '0px',
		titleSx: {
			display: 'block',
			fontFamily: 'Poppins, sans-serif',
			fontSize: '1rem',
			fontWeight: 600,
			lineHeight: 1.75,
			letterSpacing: '0.15px',
			color: 'var(--primary-dark)',
			textAlign: 'left',
		},
		stackedActionsPadding: '8px 24px',
		stackedActionsGap: '8px',
	},
	question: {
		icon: <HelpOutlineRoundedIcon fontSize='medium' />,
		color: '#6A1B9A',
		background: 'rgba(106, 27, 154, 0.12)',
		layout: 'inline',
		iconSize: '44px',
	},
}

const mergeSx = (...styles: Array<SxProps<Theme> | undefined>): SxProps<Theme> =>
	styles.filter((style): style is SxProps<Theme> => style !== undefined) as SxProps<Theme>

const CustomAlertDialog: React.FC<Readonly<CustomAlertDialogProps>> = ({
	open,
	title,
	description,
	children,
	severity = 'info',
	icon,
	hideIcon = false,
	showCloseButton = true,
	closeButtonAriaLabel = 'Close dialog',
	closeOnBackdropClick = false,
	closeOnEscapeKeyDown = true,
	onRequestClose,
	onClose,
	primaryAction,
	secondaryAction,
	actions,
	actionsContent,
	dialogTitleProps,
	dialogContentProps,
	dialogContentTextProps,
	dialogActionsProps,
	closeButtonProps,
	iconContainerSx,
	headerSx,
	PaperProps,
	fullWidth = true,
	maxWidth = 'xs',
	disableEscapeKeyDown,
	...dialogProps
}) => {
	const dialogId = useId()
	const titleId = dialogTitleProps?.id ?? `custom-alert-dialog-title-${dialogId}`
	const descriptionId = dialogContentTextProps?.id ?? `custom-alert-dialog-description-${dialogId}`
	const resolvedSeverity = severityConfig[severity]
	const resolvedIcon = hideIcon ? null : icon ?? resolvedSeverity.icon
	const resolvedActions =
		actions ?? [secondaryAction, primaryAction].filter((action): action is CustomAlertDialogAction => Boolean(action))
	const shouldRenderContent = Boolean(description || children || (!title && resolvedIcon))
	const shouldRenderActions = Boolean(actionsContent || resolvedActions.length > 0)
	const resolvedDisableEscapeKeyDown = disableEscapeKeyDown ?? closeOnEscapeKeyDown === false
	const isStackedLayout = resolvedSeverity.layout === 'stacked' && Boolean(title)
	const stackedTitleSx =
		resolvedSeverity.titleSx ?? {
			display: 'block',
			fontFamily: 'Poppins, sans-serif',
			fontSize: '1.125rem',
			fontWeight: 600,
			lineHeight: '1.55',
			color: 'var(--primary-dark)',
			textAlign: 'left',
		}

	const handleDialogClose: DialogProps['onClose'] = (event, reason) => {
		if (reason === 'backdropClick' && !closeOnBackdropClick) {
			return
		}

		onClose?.(event, reason)
		onRequestClose?.(reason)
	}

	const handleCloseButtonClick = () => {
		onRequestClose?.('closeButtonClick')
	}

	const renderAction = (action: CustomAlertDialogAction, index: number) => {
		const isLastAction = index === resolvedActions.length - 1
		const resolvedVariant = action.variant ?? (isLastAction ? 'contained' : 'text')
		const baseActionSx = isStackedLayout
			? {
					minHeight: '40px',
					minWidth: resolvedVariant === 'text' ? 'auto' : '96px',
					paddingInline: resolvedVariant === 'text' ? '8px' : '18px',
					borderRadius: '6px',
					boxShadow: 'none',
			  }
			: undefined

		return (
		<CustomButton
			key={action.id ?? `${String(action.label)}-${index}`}
			variant={resolvedVariant}
			color={action.color ?? (isLastAction ? 'primary' : isStackedLayout ? 'primary' : 'info')}
			size={action.size ?? (isStackedLayout ? 'medium' : 'small')}
			disabled={action.disabled}
			autoFocus={action.autoFocus}
			startIcon={action.startIcon}
			endIcon={action.endIcon}
			sx={mergeSx(baseActionSx, action.sx)}
			onClick={event => {
				action.onClick?.(event)
				if (action.closeOnClick !== false) {
					onRequestClose?.(action.closeReason ?? 'actionClick')
				}
			}}
			{...(action.isLoading !== undefined ? { isLoading: action.isLoading } : {})}
			{...action.buttonProps}
		>
			{action.label}
		</CustomButton>
		)
	}

	return (
		<Dialog
			open={open}
			onClose={handleDialogClose}
			fullWidth={fullWidth}
			maxWidth={maxWidth}
			role='alertdialog'
			aria-labelledby={title ? titleId : undefined}
			aria-describedby={description ? descriptionId : undefined}
			disableEscapeKeyDown={resolvedDisableEscapeKeyDown}
			PaperProps={{
				...PaperProps,
				sx: mergeSx(
					{
						width: '100%',
						borderRadius: isStackedLayout ? resolvedSeverity.paperBorderRadius ?? '16px' : '12px',
						backgroundColor: '#FFFFFF',
						backgroundImage: resolvedSeverity.paperBackground,
						backgroundRepeat: 'no-repeat',
						boxShadow:
							resolvedSeverity.paperBoxShadow ?? '0 12px 32px rgba(9, 32, 86, 0.18), 0 4px 12px rgba(9, 32, 86, 0.12)',
					},
					PaperProps?.sx,
				),
			}}
			{...dialogProps}
		>
			{(title || showCloseButton) && (
				<DialogTitle
					{...dialogTitleProps}
					id={title ? titleId : dialogTitleProps?.id}
					sx={mergeSx({ padding: isStackedLayout ? '16px 24px 0' : '20px 24px 12px' }, dialogTitleProps?.sx)}
				>
					{isStackedLayout ? (
						<Box
							sx={mergeSx(
								{
									position: 'relative',
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									gap: resolvedSeverity.stackedGap ?? '16px',
								},
								headerSx,
							)}
						>
							{showCloseButton && (
								<IconButton
									size='small'
									{...closeButtonProps}
									aria-label={closeButtonAriaLabel}
									onClick={handleCloseButtonClick}
									sx={mergeSx(
										{
											position: 'absolute',
											top: 0,
											right: 0,
											color: 'var(--primary-dark)',
										},
										closeButtonProps?.sx,
									)}
								>
									<CloseRoundedIcon fontSize='small' />
								</IconButton>
							)}
							{title && resolvedIcon && (
								<Box
									data-testid='custom-alert-dialog-icon-container'
									sx={mergeSx(
										{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											flexShrink: 0,
											width: resolvedSeverity.iconSize,
											height: resolvedSeverity.iconSize,
											borderRadius: '999px',
											color: resolvedSeverity.color,
											background: resolvedSeverity.background,
											boxShadow: resolvedSeverity.iconBoxShadow,
										},
										iconContainerSx,
									)}
								>
									{resolvedIcon}
								</Box>
							)}
							<Box
								data-testid='custom-alert-dialog-title-container'
								sx={{
									width: '100%',
									minWidth: 0,
									paddingTop: '16px',
									paddingBottom: '16px',
									paddingRight: showCloseButton ? '40px' : 0,
								}}
							>
								{typeof title === 'string' ? (
									<Typography
										component='span'
										variant='h6'
										sx={stackedTitleSx}
									>
										{title}
									</Typography>
								) : (
									title
								)}
							</Box>
						</Box>
					) : (
						<Box
							sx={mergeSx(
								{
									display: 'flex',
									alignItems: 'flex-start',
									gap: '16px',
								},
								headerSx,
							)}
						>
							{title && resolvedIcon && (
								<Box
									data-testid='custom-alert-dialog-icon-container'
									sx={mergeSx(
										{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											flexShrink: 0,
											width: resolvedSeverity.iconSize,
											height: resolvedSeverity.iconSize,
											borderRadius: '999px',
											color: resolvedSeverity.color,
											background: resolvedSeverity.background,
										},
										iconContainerSx,
									)}
								>
									{resolvedIcon}
								</Box>
							)}
							<Box sx={{ flex: 1, minWidth: 0 }}>
								{typeof title === 'string' ? (
									<Typography
										component='span'
										variant='h6'
										sx={{
											fontFamily: 'Poppins, sans-serif',
											fontSize: '1.125rem',
											fontWeight: 600,
											lineHeight: 1.4,
											color: 'var(--primary-dark)',
										}}
									>
										{title}
									</Typography>
								) : (
									title
								)}
							</Box>
							{showCloseButton && (
								<IconButton
									size='small'
									{...closeButtonProps}
									aria-label={closeButtonAriaLabel}
									onClick={handleCloseButtonClick}
									sx={mergeSx(
										{
											marginTop: '2px',
											color: 'var(--primary-dark)',
										},
										closeButtonProps?.sx,
									)}
								>
									<CloseRoundedIcon fontSize='small' />
								</IconButton>
							)}
						</Box>
					)}
				</DialogTitle>
			)}

			{shouldRenderContent && (
				<DialogContent
					{...dialogContentProps}
					sx={mergeSx(
						{
							padding: isStackedLayout ? '8px 24px 0' : title || showCloseButton ? '0 24px 8px' : '24px',
						},
						dialogContentProps?.sx,
					)}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'flex-start',
							gap: resolvedIcon && !title ? '16px' : 0,
						}}
					>
						{resolvedIcon && !title && (
							<Box
								sx={mergeSx(
									{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										flexShrink: 0,
										width: resolvedSeverity.iconSize,
										height: resolvedSeverity.iconSize,
										borderRadius: '999px',
										color: resolvedSeverity.color,
										background: resolvedSeverity.background,
										boxShadow: resolvedSeverity.iconBoxShadow,
									},
									iconContainerSx,
								)}
							>
								{resolvedIcon}
							</Box>
						)}
						<Box sx={{ flex: 1, minWidth: 0 }}>
							{description && (
								<DialogContentText
									component='div'
									{...dialogContentTextProps}
									id={descriptionId}
									sx={mergeSx(
										{
											margin: 0,
											fontFamily: 'Poppins, sans-serif',
											fontSize: '0.9375rem',
											lineHeight: 1.6,
											color: isStackedLayout ? 'rgba(9, 32, 86, 0.76)' : 'rgba(9, 32, 86, 0.8)',
										},
										dialogContentTextProps?.sx,
									)}
								>
									{description}
								</DialogContentText>
							)}
							{children}
						</Box>
					</Box>
				</DialogContent>
			)}

			{shouldRenderActions && (
				<DialogActions
					{...dialogActionsProps}
					sx={mergeSx(
						{
							padding: isStackedLayout ? resolvedSeverity.stackedActionsPadding ?? '0 24px 16px' : '8px 24px 24px',
							paddingTop:
								isStackedLayout && shouldRenderContent && !resolvedSeverity.stackedActionsPadding ? '16px' : undefined,
							gap: isStackedLayout ? resolvedSeverity.stackedActionsGap ?? '16px' : '8px',
							justifyContent: 'flex-end',
						},
						dialogActionsProps?.sx,
					)}
				>
					{actionsContent ?? resolvedActions.map(renderAction)}
				</DialogActions>
			)}
		</Dialog>
	)
}

export default CustomAlertDialog
